import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpenIcon, BarChart3Icon, TargetIcon, Loader2 } from 'lucide-react';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

const QUALITY = {
  rose: {
    bar: 'bg-gradient-to-r from-rose-400 to-rose-600 shadow-sm shadow-rose-500/30',
    track: 'bg-rose-100/90 ring-1 ring-rose-200/60',
    pill: 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/80',
    value: 'text-rose-800',
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-500/25',
    track: 'bg-amber-100/90 ring-1 ring-amber-200/60',
    pill: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80',
    value: 'text-amber-900',
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm shadow-emerald-500/25',
    track: 'bg-emerald-100/90 ring-1 ring-emerald-200/60',
    pill: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200/80',
    value: 'text-emerald-900',
  },
};

function QualityBar({ label, count, percent, tone }) {
  const q = QUALITY[tone] || QUALITY.emerald;
  const w = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/80 p-3 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
      <div className="flex justify-between gap-3 text-xs">
        <span className={`inline-flex max-w-[65%] items-center rounded-lg px-2 py-0.5 font-semibold ${q.pill}`}>{label}</span>
        <span className={`shrink-0 font-bold tabular-nums ${q.value}`}>
          {fmt(count)} · {pct(percent)}
        </span>
      </div>
      <div className={`mt-2.5 h-2.5 overflow-hidden rounded-full ${q.track}`}>
        <div className={`h-full rounded-full transition-all duration-500 ease-out ${q.bar}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, variant = 'emerald' }) {
  const variants = {
    emerald: {
      card: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/70 ring-emerald-500/10',
      glow: 'from-emerald-400/20',
      icon: 'bg-emerald-500/15 text-emerald-700 ring-emerald-400/30',
      label: 'text-emerald-900/75',
      val: 'text-emerald-950',
    },
    lime: {
      card: 'border-lime-200/80 bg-gradient-to-br from-lime-50 via-white to-emerald-50/60 ring-lime-500/10',
      glow: 'from-lime-400/20',
      icon: 'bg-lime-500/15 text-lime-800 ring-lime-400/30',
      label: 'text-lime-900/75',
      val: 'text-lime-950',
    },
  };
  const v = variants[variant] || variants.emerald;
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 shadow-md ring-1 transition hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}>
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`} />
      <div className="relative">
        {Icon && (
          <span className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1 text-2xl font-black tabular-nums tracking-tight ${v.val}`}>{value}</p>
      </div>
    </div>
  );
}

/**
 * بلوك إحصائيات التمارين اليومية — بيانات من /education/student-dashboard/daily-exercises
 */
const StudentDashboardDailyExercises = ({ data, loading, isRTL }) => {
  const { t } = useTranslation();

  const dm = data?.metrics;
  const period = data?.period;
  const buckets = dm?.attemptQualityBuckets;
  const periodLabel =
    period?.seasonYearLabel || (period?.start && period?.end ? `${period.start} → ${period.end}` : null);

  return (
    <section id="student-dashboard-daily" className="scroll-mt-24 overflow-visible rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
      <div className="overflow-hidden rounded-t-[1.2rem] border-b border-emerald-200/50 bg-gradient-to-r from-emerald-500/15 via-teal-50/80 to-lime-50/60 px-4 py-3 sm:px-5">
        <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 ring-2 ring-white/50">
            <BookOpenIcon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-black tracking-tight text-emerald-950 sm:text-lg">{t('users.students.stats.dailyExercisesPeriod')}</h2>
            {periodLabel && <p className="mt-0.5 truncate text-xs font-semibold text-emerald-800/80">{periodLabel}</p>}
          </div>
        </div>
      </div>
      <div className="relative p-4 sm:p-5">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-[1.2rem] bg-white/75 backdrop-blur-[2px]">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-600" strokeWidth={2.5} aria-hidden />
          </div>
        )}
        {dm ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <MetricTile icon={BarChart3Icon} label={t('dashboard.stats.totalSessions')} value={fmt(dm.totalSessions)} variant="emerald" />
              <MetricTile icon={TargetIcon} label={t('dashboard.stats.totalAnswers')} value={fmt(dm.totalAnswers)} variant="lime" />
            </div>
            {buckets && (
              <div className="mt-6 space-y-3">
                <h3 className="inline-flex rounded-lg bg-emerald-100/80 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-900 ring-1 ring-emerald-300/50">
                  {t('dashboard.stats.attemptQuality')}
                </h3>
                <QualityBar
                  label={t('users.students.stats.qualityWeakUnder50')}
                  count={buckets.weakUnder50Percent?.count}
                  percent={buckets.weakUnder50Percent?.percentOfItems}
                  tone="rose"
                />
                <QualityBar
                  label={t('users.students.stats.qualityMedium50to69')}
                  count={buckets.medium50To69Percent?.count}
                  percent={buckets.medium50To69Percent?.percentOfItems}
                  tone="amber"
                />
                <QualityBar
                  label={t('users.students.stats.qualityGood70Plus')}
                  count={buckets.good70PlusPercent?.count}
                  percent={buckets.good70PlusPercent?.percentOfItems}
                  tone="emerald"
                />
              </div>
            )}
          </>
        ) : (
          !loading && <p className="text-sm font-semibold text-slate-500">{t('users.students.stats.noData')}</p>
        )}
      </div>
    </section>
  );
};

export default StudentDashboardDailyExercises;
